import React, { useState, useRef, useEffect } from "react";
import { LiveKitRoom, VideoConference } from "@livekit/components-react";
import { Room } from "livekit-client";
import { getCookie } from "../../../../api/cookie";
import { CallButton } from "./CallButton";
import { fetchLiveKitToken } from "./livekit-utils";
import { logger } from "../../../../utils/logger";
import { generateRoomName, cleanPhoneNumber, getPhoneNumberId } from "../../../../utils/callUtils";
import { CallHeader } from "./CallHeader";
import { CallStatusView } from "./CallStatusView";
import { getEmbeddedSignupByPhone } from "../../../../api/login.service";

interface PhoneCallsTabProps {
  customerData: { phone: string; name: string } | null;
  participantIdentity?: string | null;
  participantName?: string | null;
}

export function PhoneCallsTab({
  customerData,
  participantIdentity,
  participantName,
}: PhoneCallsTabProps) {
  const [token, setToken] = useState<string | null>(null);
  const [serverUrl, setServerUrl] = useState<string>("");
  const [roomName, setRoomName] = useState<string>("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [shouldConnect, setShouldConnect] = useState(false);
  const roomRef = useRef<Room | null>(null);
  const [phoneNumberFromArray, setPhoneNumberFromArray] = useState<string | null>(null);
  const [isLoadingPhoneNumber, setIsLoadingPhoneNumber] = useState(true);

  // Fetch phone_numbers array from API
  useEffect(() => {
    const fetchPhoneNumbers = async () => {
      setIsLoadingPhoneNumber(true);
      try {
        const displayPhoneNumber = sessionStorage.getItem('display_phone_number');
        if (displayPhoneNumber) {
          logger.log('PhoneCallsTab: Fetching phone_numbers...', { displayPhoneNumber });
          const embeddedData = await getEmbeddedSignupByPhone(displayPhoneNumber);
          logger.log('PhoneCallsTab: Embedded data received', embeddedData);
          if (embeddedData?.records && embeddedData.records.length > 0) {
            const record = embeddedData.records[0];
            const phoneNumbers = record.phone_numbers || [];
            logger.log('PhoneCallsTab: Phone numbers found', phoneNumbers);
            if (phoneNumbers.length > 0) {
              // Use first phone number from array
              setPhoneNumberFromArray(phoneNumbers[0]);
              logger.log('PhoneCallsTab: Phone number set', phoneNumbers[0]);
            }
          }
        }
      } catch (err) {
        logger.error('Failed to fetch phone_numbers:', err);
      } finally {
        setIsLoadingPhoneNumber(false);
      }
    };
    fetchPhoneNumbers();
  }, []);

  const handleInitiateCall = async () => {
    if (!customerData) {
      logger.warn("PhoneCallsTab: No customer data available");
      setError("No customer selected");
      return;
    }
    
    // Only prevent if actually connected, allow retry if just token exists
    if (isConnected) {
      logger.warn("PhoneCallsTab: Already connected");
      return;
    }
    
    // If token exists but not connected, allow retry by clearing token first
    if (token && !isConnected) {
      logger.log("PhoneCallsTab: Clearing existing token for retry");
      setToken(null);
      setServerUrl("");
      setShouldConnect(false);
    }

    try {
      setError(null);
      setIsConnecting(true);
      
      logger.log("PhoneCallsTab: Initiating call...", {
        customerData,
        phoneNumberFromArray,
        hasToken: !!getCookie("token")
      });
      
      const customerPhone = cleanPhoneNumber(customerData.phone);
      
      if (!customerPhone) {
        throw new Error("Customer phone number not found");
      }
      
      // For phone calls: use phone number from phone_numbers array instead of phoneNumberId
      // Format: user_9240059132_calling_coming_4917615687979
      // Wait for phone number to load if still loading
      if (isLoadingPhoneNumber) {
        throw new Error("Loading phone number. Please wait...");
      }
      
      const phoneNumberForRoom = phoneNumberFromArray || getPhoneNumberId();
      if (!phoneNumberForRoom) {
        throw new Error("Phone number not found. Please refresh the page.");
      }
      
      logger.log("PhoneCallsTab: Generating room name...", {
        phoneNumberForRoom,
        customerPhone
      });
      
      const generatedRoomName = generateRoomName(phoneNumberForRoom, customerPhone);
      const participantToken = getCookie("token") || "";
      
      if (!participantToken) {
        throw new Error("Authentication token not found. Please log in again.");
      }
      
      // For phone calls: use phone number from phone_numbers array in participantIdentity
      // Format: user_9240059132 (from phone_numbers array)
      const phoneNumberForIdentity = phoneNumberFromArray || customerPhone;
      const finalParticipantIdentity = participantIdentity || `user_${phoneNumberForIdentity}`;
      const finalParticipantName = participantName || "user";

      logger.log("PhoneCallsTab: Calling fetchLiveKitToken...", {
        generatedRoomName,
        finalParticipantName,
        finalParticipantIdentity
      });

      const response = await fetchLiveKitToken(
        participantToken,
        generatedRoomName,
        finalParticipantName,
        finalParticipantIdentity
      );
      
      logger.log("PhoneCallsTab: LiveKit token received", {
        hasToken: !!response.token,
        serverUrl: response.serverUrl
      });
      
      setToken(response.token);
      setServerUrl(response.serverUrl);
      setRoomName(response.roomName);
      setShouldConnect(true);
      setIsConnecting(false);
    } catch (err: any) {
      logger.error("Error initiating phone call:", err);
      setError(err?.message || "Failed to initialize call");
      setIsConnecting(false);
    }
  };

  // Removed auto-connect on mount - connection only happens when Call button is clicked

  const handleRoomConnected = () => {
    setIsConnected(true);
    setError(null);
  };

  const handleRoomDisconnected = () => {
    setIsConnected(false);
    setShouldConnect(false);
    roomRef.current = null;
  };

  const handleDisconnect = async () => {
    try {
      // Properly disconnect from LiveKit room by setting connect to false
      // This will trigger the room to disconnect and call handleRoomDisconnected
      setShouldConnect(false);
      
      // Also try to disconnect the room instance if we have it
      if (roomRef.current) {
        try {
          await roomRef.current.disconnect();
        } catch (err) {
          logger.warn("Room disconnect error:", err);
        }
      }
      
      setTimeout(() => {
        setToken(null);
        setServerUrl("");
        setIsConnected(false);
        setError(null);
        roomRef.current = null;
      }, 100);
    } catch (err: any) {
      logger.error("Error disconnecting from room:", err);
      setShouldConnect(false);
      setToken(null);
      setServerUrl("");
      setIsConnected(false);
      setError(null);
      roomRef.current = null;
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <CallHeader
        title={`Phone Calls${phoneNumberFromArray ? ` (${phoneNumberFromArray})` : ''}`}
        isCalling={isConnecting}
        isConnected={isConnected}
        hasIncomingCall={false}
        hasCustomer={!!customerData}
        onCall={handleInitiateCall}
        onDisconnect={handleDisconnect}
        callButtonVariant="blue"
      />
      <div className="flex-1 flex flex-col">
        <CallStatusView error={error} isCalling={isConnecting} hasToken={!!token}>
          {token && serverUrl && shouldConnect ? (
            <>
              {/* @ts-ignore - LiveKitRoom has ReactNode return type which includes string, but works fine at runtime */}
              <LiveKitRoom
                video={false}
                audio={true}
                token={token}
                serverUrl={serverUrl}
                connect={shouldConnect}
                onConnected={handleRoomConnected}
                onDisconnected={handleRoomDisconnected}
                options={{
                  adaptiveStream: true,
                  dynacast: true,
                  publishDefaults: {
                    videoSimulcastLayers: [],
                  },
                }}
                className="flex-1"
              >
                <VideoConference />
              </LiveKitRoom>
            </>
          ) : (
            <div className="flex items-center justify-center p-8">
              <div className="text-center">
                <div className="text-4xl mb-4">☎️</div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Phone Calls{phoneNumberFromArray ? ` (${phoneNumberFromArray})` : ''}
                </h3>
                <p className="text-gray-500">Click the Call button to start a phone call</p>
              </div>
            </div>
          )}
        </CallStatusView>
      </div>
    </div>
  );
}

